import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useCallback } from "react";
import { useNavigate, useParams } from "react-router";

import type { ConstraintListItemDto, ServicePartnerCreateRequest, ServicePartnerRequest } from "@/api/types";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { createFormPermissions, toPermissionsRequest } from "@/helpers/permissions";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { useBool } from "@/hooks/useBool";
import { useSlug } from "@/hooks/useSlug";
import { usePermissionQueries } from "@/queries/permissions/queryOptions";
import { useProjectConnectionQueries } from "@/queries/project-connections/queryOptions";
import { servicePartnerMutations } from "@/queries/service-partners/mutations";
import { useServicePartnersQueries } from "@/queries/service-partners/queryOptions";
import { useServiceQueries } from "@/queries/services/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { type CreateOrEditServicePartnerFormValues, ServicePartnerForm } from "../components/ServicePartnerForm";

export function EditServicePartnerPage(): React.ReactNode {
  const sessionUser = useSessionUser();
  const slug = useSlug();
  const { id: serviceId } = useParams<{ id: string }>();
  const serviceIdForQuery = serviceId ?? "";
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const navigate = useNavigate();
  const [isSubmitting, submittingHandler] = useBool(false);

  const serviceQueries = useServiceQueries();
  const servicePartnersQueries = useServicePartnersQueries();
  const permissionQueries = usePermissionQueries();

  const {
    data: categories = [],
    isFetching: isCategoriesFetching,
    error: categoriesError,
  } = useQuery(servicePartnersQueries.categories());
  const {
    data: serviceDetails,
    isFetching: isServiceDetailsFetching,
    error: servicePartnerError,
  } = useQuery({
    ...serviceQueries.details(
      {
        serviceId: serviceIdForQuery,
      },
      {
        translationMode: "original",
      },
    ),
  });

  const {
    data: roles = [],
    isFetching: isRolesFetching,
    error: rolesError,
  } = useQuery({
    ...permissionQueries.listRoles(),
    enabled: sessionUser.isAdmin,
  });
  const {
    data: defaultsServicesPermissions = [],
    isFetching: isDefaultPermissionsFetching,
    error: servicePartnersDefaultPermissionsError,
  } = useQuery({
    ...servicePartnersQueries.defaultPermissions(),
    enabled: sessionUser.isAdmin,
  });
  const connectionsQuery = { Limit: 1000, Offset: 0 };
  const projectConnectionQueries = useProjectConnectionQueries();
  const {
    data: connections,
    isFetching: isConnectionsFetching,
    error: connectionsError,
  } = useQuery({
    ...projectConnectionQueries.list(connectionsQuery),
    enabled: sessionUser.isAdmin,
  });

  const updateServicePartnerMutation = servicePartnerMutations.useUpdateServicePartner();

  const { uploadFormImage } = useUploadImage();

  const onSubmit = useCallback(
    async function (data: CreateOrEditServicePartnerFormValues) {
      submittingHandler.setTrue();

      if (data.profileImage == null || data.profileImage.length == 0 || data.category?.id == null) {
        showFlashToast({
          variant: "alert",
          message: t("model.service.action.create.notification.error"),
        });

        return;
      }

      let audience: ConstraintListItemDto[] = [];
      if (!data.connection && sessionUser.isAdmin && data.type !== "iqbi") {
        audience = data.audience;
      }

      const profileImage = await uploadFormImage(data.profileImage[0]);

      if (!profileImage) return;

      const payload: ServicePartnerRequest | ServicePartnerCreateRequest = {
        title: data.title.trim(),
        subtitle: data.subtitle.trim(),
        description: data.description.trim(),
        audience,
        projectConnectionId: data.connection?.id,
        category: data.category.id,
        email: data.email?.trim(),
        phoneNumber: data.phoneNumber?.trim(),
        profileImageId: profileImage.id,
        permissions: sessionUser.isAdmin ? toPermissionsRequest(data.permissions) : [],
        type: data.type,
      };

      if (data.headerImage && data.headerImage.length > 0) {
        const headerImage = await uploadFormImage(data.headerImage[0]);

        payload.headerImageId = headerImage?.id;
      }

      try {
        await updateServicePartnerMutation.mutateAsync({ id: serviceIdForQuery, data: payload });
        void navigate(routes.servicePartners.details({ slug, id: serviceIdForQuery }));
      } catch {
        submittingHandler.setFalse();
      }
    },
    [
      submittingHandler,
      sessionUser.isAdmin,
      uploadFormImage,
      showFlashToast,
      t,
      updateServicePartnerMutation,
      serviceIdForQuery,
      navigate,
      slug,
    ],
  );

  const error =
    servicePartnerError || categoriesError || rolesError || servicePartnersDefaultPermissionsError || connectionsError;
  if (error) {
    return <ErrorPage error={error} />;
  }

  const isPageLoading =
    isCategoriesFetching ||
    isDefaultPermissionsFetching ||
    isRolesFetching ||
    isServiceDetailsFetching ||
    isConnectionsFetching;
  if (isPageLoading) {
    return <FullSizeLoader withPadding />;
  }

  const canAccessPage = serviceDetails?.canEdit;

  if (!canAccessPage) {
    return <ErrorPage status={403} />;
  }

  return (
    <ServicePartnerForm
      contentLanguageId={serviceDetails?.originalLanguageId}
      isEditMode
      categories={categories}
      isSubmitting={isSubmitting}
      connections={connections?.items ?? []}
      allowedTypes={[]}
      defaultValues={{
        title: serviceDetails?.title || "",
        subtitle: serviceDetails?.subtitle || "",
        description: serviceDetails?.description || "",
        audience: serviceDetails?.audience || [],
        connection: serviceDetails?.connection
          ? connections?.items.find((c) => c.id === serviceDetails.connection?.id)
          : undefined,
        category: serviceDetails?.category,
        permissions: createFormPermissions(roles, defaultsServicesPermissions, serviceDetails?.permissions),
        email: serviceDetails?.email,
        headerImage: serviceDetails?.headerImage ? [serviceDetails.headerImage] : [],
        profileImage: serviceDetails?.profileImage ? [serviceDetails.profileImage] : [],
        phoneNumber: serviceDetails?.phoneNumber,
        type: serviceDetails?.type || "general",
      }}
      onSubmit={onSubmit}
    />
  );
}
